import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/Badge";
import type { CoachingClip, CoachingNote } from "@/lib/types";
import { formatPlayhead } from "@/features/playback/useVideoPlayhead";
import {
  clamp,
  clipSeverity,
  formatClock,
  formatDuration,
  FRAME,
  MAX_CLIP_DURATION_SECONDS,
  RECOMMENDED_CLIP_SECONDS,
  snapTime,
  type ClipWindow,
} from "../clipTrack";
import { RepeatIcon } from "./icons";

// ════════════════════════════════════════════════════════════════════════
// ClipTrack — NEW (Coach Review v2 layout).
// ────────────────────────────────────────────────────────────────────────
// The scrub track in the main content column. Two stacked rows:
//   1. (clip mode only) existing-clips lane — cyan blocks per saved
//      clip on this match.
//   2. the scrub track itself — a click/drag playhead, note marks
//      (click to jump), and — in clip mode — a draggable IN/OUT clip
//      window with a duration badge.
//
// All times are ABSOLUTE SECONDS. The track drives the real <video>
// element through the parent's `onSeek` callback; the parent also
// owns the clip window state so the clip composer + preset bar stay
// in sync. IN/OUT drag snaps to round seconds and existing note
// marks (see `snapTime`).
//
// Per the react-konva-canvas-patterns guidance, the in-flight drag
// position is kept off React state where it would churn — but this
// track is a plain DOM component (no Konva), and the drag must update
// the visible window every pointermove, so the window IS React state
// here, lifted to the parent. That's correct: a ~60Hz setState on a
// handful of nodes is cheap; the Konva caveat is about per-stroke
// canvas state, which this isn't.
// ════════════════════════════════════════════════════════════════════════

export type ClipTrackProps = {
  /** Total media duration, in seconds. 0 until the video loads. */
  duration: number;
  /** Current playhead position, in seconds. */
  playhead: number;
  /** Scrub / click the track → seek the video. */
  onSeek: (seconds: number) => void;
  /** The active clip window (absolute seconds), or null. */
  clipWindow: ClipWindow | null;
  /** Drag IN/OUT → update the window. */
  onClipWindowChange: (window: ClipWindow) => void;
  /** Whether clip mode is active (shows the window + clips lane). */
  inClipMode: boolean;
  /** This match's video notes — rendered as click-to-jump marks. */
  notes: CoachingNote[];
  /** This match's saved clips — rendered in the clips lane. */
  clips: CoachingClip[];
  /** The note last seeked to — its mark gets the active ring. */
  activeNoteId: number | null;
  /** Click a mark → jump to that note. */
  onJumpToNote: (note: CoachingNote) => void;
  /** Loop-within-window toggle state. */
  loop: boolean;
  /** Toggle loop. */
  onLoopChange: (loop: boolean) => void;
};

type DragKind = "playhead" | "in" | "out";

export function ClipTrack({
  duration,
  playhead,
  onSeek,
  clipWindow,
  onClipWindowChange,
  inClipMode,
  notes,
  clips,
  activeNoteId,
  onJumpToNote,
  loop,
  onLoopChange,
}: ClipTrackProps) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState<DragKind | null>(null);

  // The track only has a meaningful timeline once the video has
  // loaded its metadata (`duration > 0`). Before that — no match
  // picked, or the video file still loading — there's nothing to
  // map seconds onto, so note marks / the playhead / the clip
  // window are not rendered (mapping them against a fake span would
  // jam every mark at one edge). The track still shows its rail as
  // a clear "waiting for video" affordance.
  const hasTimeline = duration > 0;
  const dur = hasTimeline ? duration : 1;
  const pct = (seconds: number) => `${clamp(seconds / dur, 0, 1) * 100}%`;

  // Note marks as absolute seconds — fed to the IN/OUT snap.
  const noteSeconds = notes
    .map((n) => Number(n.timestamp_seconds ?? -1))
    .filter((t) => t >= 0);

  /** Map a clientX pixel to an absolute time on the track. */
  const posToTime = useCallback(
    (clientX: number): number => {
      const el = trackRef.current;
      if (!el) return 0;
      const r = el.getBoundingClientRect();
      return clamp((clientX - r.left) / r.width, 0, 1) * dur;
    },
    [dur],
  );

  // Window drag — a pointermove/up pair on `window`, active only
  // while a handle (or the playhead) is being dragged.
  useEffect(() => {
    if (!dragging) return;
    const onMove = (e: PointerEvent) => {
      const t = posToTime(e.clientX);
      if (dragging === "playhead") {
        onSeek(t);
        return;
      }
      if (!clipWindow) return;
      if (dragging === "in") {
        const snapped = snapTime(t, noteSeconds);
        onClipWindowChange({
          start: Math.min(snapped, clipWindow.end - FRAME * 5),
          end: clipWindow.end,
        });
      } else {
        const snapped = snapTime(t, noteSeconds);
        onClipWindowChange({
          start: clipWindow.start,
          end: Math.max(snapped, clipWindow.start + FRAME * 5),
        });
      }
    };
    const onUp = () => setDragging(null);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
    };
  }, [dragging, clipWindow, posToTime, noteSeconds, onClipWindowChange, onSeek]);

  const clipDuration = clipWindow ? clipWindow.end - clipWindow.start : 0;
  const severity = clipSeverity(clipWindow);

  return (
    <div className="flex flex-col gap-2" data-testid="coach-review-clip-track">
      {/* Existing-clips lane — clip mode only. */}
      {inClipMode && clips.length > 0 ? (
        <div className="flex items-center gap-2">
          <span className="w-20 text-right font-mono text-[10px] uppercase tracking-mono text-ink-muted">
            Clips
          </span>
          <div className="relative h-2 flex-1 rounded bg-field">
            {clips.map((c) => (
              <div
                key={c.id}
                title={`${c.title} · ${formatDuration(c.duration_seconds)}`}
                data-testid="clip-track-existing-clip"
                className="absolute inset-y-0 rounded bg-accent/45"
                style={{
                  left: pct(c.start_seconds),
                  width: pct(c.end_seconds - c.start_seconds),
                }}
              />
            ))}
          </div>
        </div>
      ) : null}

      {/* Scrub track. */}
      <div className="flex items-center gap-2">
        <span className="w-20 text-right font-mono text-[11px] text-ink-secondary tabular-nums">
          {formatClock(playhead)}
        </span>
        <div
          ref={trackRef}
          data-testid="clip-track-scrub"
          onPointerDown={(e) => {
            // No timeline yet → the track is inert (nothing to seek).
            if (!hasTimeline) return;
            onSeek(posToTime(e.clientX));
            setDragging("playhead");
          }}
          className={[
            "relative flex-1 rounded border border-rule bg-elevated",
            hasTimeline ? "cursor-pointer" : "cursor-default",
          ].join(" ")}
          style={{ height: inClipMode ? 56 : 36 }}
        >
          {/* Base rail. */}
          <div className="absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded bg-rule-strong" />

          {/* Before the video loads its metadata there's no timeline
           * to map onto — show a neutral hint instead of jamming the
           * playhead + marks at one edge. */}
          {!hasTimeline ? (
            <span
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-mono text-[10px] uppercase tracking-mono text-ink-muted"
              data-testid="clip-track-no-video"
            >
              Select a match to load the video
            </span>
          ) : null}

          {/* Progress fill (non-clip mode). */}
          {hasTimeline && !inClipMode ? (
            <div
              className="absolute top-1/2 left-0 h-1 -translate-y-1/2 rounded bg-accent"
              style={{ width: pct(playhead) }}
            />
          ) : null}

          {/* Clip window + handles (clip mode). */}
          {hasTimeline && inClipMode && clipWindow ? (
            <>
              {/* Dimmed lead-in / lead-out. */}
              <div
                className="absolute inset-y-1.5 left-0 bg-navy-900/40"
                style={{ width: pct(clipWindow.start) }}
              />
              <div
                className="absolute inset-y-1.5 right-0 bg-navy-900/40"
                style={{ width: pct(dur - clipWindow.end) }}
              />
              {/* Selected window. */}
              <div
                data-testid="clip-track-window"
                className={[
                  "absolute inset-y-1.5 rounded border-2",
                  severity === "over"
                    ? "border-destructive bg-destructive/20"
                    : "border-accent bg-accent/20",
                ].join(" ")}
                style={{
                  left: pct(clipWindow.start),
                  width: pct(clipDuration),
                }}
              >
                <span
                  className={[
                    "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 whitespace-nowrap",
                    "rounded-sm bg-surface/95 px-2 py-0.5 font-mono text-[11px] font-bold",
                    severity === "over" ? "text-destructive" : "text-ink",
                  ].join(" ")}
                >
                  {formatDuration(clipDuration)}
                </span>
              </div>
              <ClipHandle
                kind="in"
                position={pct(clipWindow.start)}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  setDragging("in");
                }}
              />
              <ClipHandle
                kind="out"
                position={pct(clipWindow.end)}
                onPointerDown={(e) => {
                  e.stopPropagation();
                  setDragging("out");
                }}
              />
            </>
          ) : null}

          {/* Playhead line — only once the timeline exists. */}
          {hasTimeline ? (
            <div
              className="pointer-events-none absolute inset-y-0 w-0.5 -ml-px bg-paper shadow-[0_0_0_1px_var(--ink-primary)]"
              style={{ left: pct(playhead) }}
              data-testid="clip-track-playhead"
            />
          ) : null}

          {/* Note marks — only once the timeline exists; before that
           * every mark would map to the same edge. */}
          {hasTimeline && notes.map((n) => {
            const t = Number(n.timestamp_seconds ?? -1);
            if (t < 0) return null;
            const kind =
              n.note_type === "positive"
                ? "good"
                : n.note_type === "correction"
                  ? "flag"
                  : "note";
            const active = activeNoteId === n.id;
            return (
              <button
                key={n.id}
                type="button"
                title={`${formatClock(t)} · ${n.title}`}
                data-testid="clip-track-note-mark"
                data-coach-note-id={n.id}
                onClick={(e) => {
                  e.stopPropagation();
                  onJumpToNote(n);
                }}
                className={[
                  "absolute bottom-1 h-3 w-3 -translate-x-1/2 rounded-full p-0",
                  "shadow-[0_0_0_2px_var(--bg-elevated)]",
                  kind === "flag"
                    ? "bg-flag"
                    : kind === "good"
                      ? "bg-good"
                      : "bg-accent",
                  active ? "ring-2 ring-ink" : "",
                ].join(" ")}
                style={{ left: pct(t) }}
              />
            );
          })}
        </div>
        <span className="w-20 font-mono text-[11px] text-ink-muted tabular-nums">
          {formatClock(duration)}
        </span>
      </div>

      {/* Window readout + loop toggle — clip mode only. */}
      {inClipMode && clipWindow ? (
        <div
          className="flex items-center gap-3 rounded bg-elevated px-2.5 py-1.5 font-mono text-[11px]"
          data-testid="clip-track-readout"
        >
          <span className="text-ink-muted">IN</span>
          <span className="text-ink">{formatPlayhead(clipWindow.start)}</span>
          <span className="ml-2 text-ink-muted">OUT</span>
          <span className="text-ink">{formatPlayhead(clipWindow.end)}</span>
          <span className="ml-2 text-ink-muted">DUR</span>
          <span
            className={
              severity === "over"
                ? "font-bold text-destructive"
                : "font-bold text-ink"
            }
          >
            {formatDuration(clipDuration)}
          </span>
          {severity === "over" ? (
            <Badge variant="flag" className="bg-destructive text-paper">
              Over {MAX_CLIP_DURATION_SECONDS}s
            </Badge>
          ) : severity === "warn" ? (
            <Badge variant="flag">Over {RECOMMENDED_CLIP_SECONDS}s</Badge>
          ) : null}
          <button
            type="button"
            onClick={() => onLoopChange(!loop)}
            aria-pressed={loop}
            data-testid="clip-track-loop"
            className={[
              "ml-auto inline-flex items-center gap-1.5 rounded px-2.5 py-1",
              "font-display text-[11px] font-semibold transition-colors",
              loop
                ? "bg-accent text-accent-ink"
                : "bg-field text-ink-secondary hover:text-ink",
            ].join(" ")}
          >
            <RepeatIcon size={11} />
            Loop
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** One IN/OUT drag handle — a thin accent bar with a labelled tab. */
function ClipHandle({
  kind,
  position,
  onPointerDown,
}: {
  kind: "in" | "out";
  position: string;
  onPointerDown: (e: React.PointerEvent) => void;
}) {
  return (
    <div
      onPointerDown={onPointerDown}
      data-testid={`clip-track-handle-${kind}`}
      className="absolute inset-y-0.5 z-[2] -ml-[7px] flex w-3.5 cursor-ew-resize items-center justify-center"
      style={{ left: position }}
    >
      <div className="h-full w-1 rounded bg-accent shadow-[0_0_0_1px_var(--ink-primary)]" />
      <span
        className={[
          "pointer-events-none absolute -top-1.5 rounded-sm bg-ink px-1.5 py-0.5",
          "font-mono text-[9px] font-bold uppercase tracking-mono text-accent",
          kind === "in" ? "-left-1" : "-right-1",
        ].join(" ")}
      >
        {kind}
      </span>
    </div>
  );
}
